import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  DollarSign,
  Users,
  TrendingUp,
  Plus,
  Search,
  Bell,
  ChevronRight,
  Check
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

// Clean dashboard - mobile
const CleanMobile = () => (
  <div className="md:hidden bg-card rounded-md p-3 border border-improvement/30">
    <div className="flex items-center justify-between mb-3">
      <div>
        <span className="font-heading font-semibold text-foreground text-sm">Dashboard</span>
        <p className="text-[9px] text-muted-foreground">What matters today.</p>
      </div>
      <div className="flex items-center gap-1.5">
        <button className="p-1.5 text-muted-foreground"><Search size={14} /></button>
        <button className="p-1.5 text-muted-foreground relative">
          <Bell size={14} />
          <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-improvement rounded-full" />
        </button>
        <button className="px-2 py-1 bg-primary text-primary-foreground text-[9px] rounded">Create</button>
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-2 mb-3">
      <div className="bg-improvement-light rounded p-2 border border-improvement/20">
        <p className="font-heading font-bold text-foreground text-sm">$24,521</p>
        <p className="text-[8px] text-muted-foreground">Revenue</p>
      </div>
      <div className="bg-secondary rounded p-2">
        <p className="font-heading font-bold text-foreground text-sm">1,429</p>
        <p className="text-[8px] text-muted-foreground">Users</p>
      </div>
      <div className="bg-secondary rounded p-2">
        <p className="font-heading font-bold text-foreground text-sm">342</p>
        <p className="text-[8px] text-muted-foreground">Conversions</p>
      </div>
    </div>
    
    <div className="flex items-center justify-between pt-2 border-t border-border">
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 bg-improvement rounded-full" />
        <span className="text-[9px] text-muted-foreground">3 tasks pending</span>
      </div>
      <button className="text-[9px] text-primary">View →</button>
    </div>
  </div>
);

// Clean dashboard - desktop
const CleanDesktop = () => (
  <div className="hidden md:block bg-card rounded-md p-5 border border-improvement/30">
    <div className="flex items-center justify-between mb-5">
      <div>
        <h3 className="font-heading font-semibold text-foreground text-lg">Dashboard</h3>
        <p className="text-xs text-muted-foreground">Here is what matters today.</p>
      </div>
      <div className="flex items-center gap-2">
        <button className="p-2 text-muted-foreground hover:bg-secondary rounded"><Search size={16} /></button>
        <button className="p-2 text-muted-foreground hover:bg-secondary rounded relative">
          <Bell size={16} />
          <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-improvement rounded-full" />
        </button>
        <Button variant="clarity" size="sm">
          <Plus size={14} className="mr-1" />Create New
        </Button>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3 mb-5">
      <div className="bg-improvement-light rounded-md p-4 border border-improvement/20">
        <div className="flex items-center justify-between mb-2">
          <div className="p-1.5 bg-improvement/10 rounded"><DollarSign size={14} className="text-improvement" /></div>
          <span className="text-xs text-improvement font-medium">+12%</span>
        </div>
        <p className="font-heading font-bold text-foreground text-xl">$24,521</p>
        <p className="text-xs text-muted-foreground">Total Revenue</p>
      </div>
      <div className="bg-secondary rounded-md p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="p-1.5 bg-primary/10 rounded"><Users size={14} className="text-primary" /></div>
          <span className="text-xs text-primary font-medium">+8%</span>
        </div>
        <p className="font-heading font-bold text-foreground text-xl">1,429</p>
        <p className="text-xs text-muted-foreground">Active Users</p>
      </div>
      <div className="bg-secondary rounded-md p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="p-1.5 bg-primary/10 rounded"><TrendingUp size={14} className="text-primary" /></div>
          <span className="text-xs text-primary font-medium">+5%</span>
        </div>
        <p className="font-heading font-bold text-foreground text-xl">342</p>
        <p className="text-xs text-muted-foreground">Conversions</p>
      </div>
    </div>

    <div className="bg-secondary/50 rounded-md p-4 border border-border mb-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-heading font-semibold text-foreground text-sm">This Week</span>
        <div className="flex gap-1">
          <button className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded">Week</button>
          <button className="px-2 py-1 text-xs text-muted-foreground hover:bg-secondary rounded">Month</button>
        </div>
      </div>
      <div className="flex items-end justify-between h-24 gap-2">
        {[65, 45, 78, 88, 95, 52, 48].map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center">
            <div className="w-full bg-primary/80 rounded-t" style={{ height: `${h}%` }} />
            <span className="text-[9px] text-muted-foreground mt-1">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="flex items-center justify-between pt-3 border-t border-border">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-improvement rounded-full" />
        <span className="text-xs text-muted-foreground">3 tasks need attention</span>
      </div>
      <Button variant="ghost" size="sm" className="text-xs group">
        View Tasks
        <ChevronRight size={12} className="ml-1 group-hover:translate-x-0.5 transition-transform" />
      </Button>
    </div>
  </div>
);

const FocusItem = ({ title, desc }) => (
  <div className="flex items-start gap-2">
    <div className="w-4 h-4 rounded-full bg-improvement-light flex items-center justify-center flex-shrink-0 mt-0.5">
      <Check size={10} className="text-improvement" />
    </div>
    <div>
      <p className="font-medium text-foreground text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
  </div>
);

export const ClaritySection = () => {
  const focuses = [
    { title: "One primary action", desc: "Create New is clear" },
    { title: "One secondary action", desc: "View Tasks for follow-up" },
    { title: "Supporting insight", desc: "Chart shows context" },
    { title: "Clear hierarchy", desc: "Revenue highlighted" }
  ];

  return (
    <section id="clarity" className="zone-resolution section-tight">
      <div className="container-wide">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-5">
          <p className="text-[11px] font-medium text-improvement tracking-wide uppercase mb-1.5">Focus</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
            Clarifying What Matters
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl">
            When everything is important, nothing is.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <CleanMobile />
          <CleanDesktop />
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-5">
          <div className="bg-improvement-light/30 rounded-md border border-improvement/20 p-4 max-w-md">
            <h4 className="font-heading font-semibold text-foreground text-sm mb-3">What Changed</h4>
            <div className="grid grid-cols-2 gap-3">
              {focuses.map((f, i) => <FocusItem key={i} {...f} />)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClaritySection;
